import {
  Box,
  Button,
  Text,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Flex,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useContext, useRef } from "react";
import { getRoleById, deleteRole } from "../../actions/role.actions";
import { AuthContext } from "../../providers/AuthValidationLayer";
import { Unauthorized } from "../../components/Unauthorized";
import { Loading } from "../../components/Loading";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { DeleteAlert } from "../../components/DeleteAlert";
import { getDashboardData } from "../../actions/dashboard.action";
import { fetchRoles, updateRole } from "../../actions/role.actions";
import { useToast } from "@chakra-ui/react";
import { normalizeRoleAccessSections } from "../../utils/permissionAliases";

export default function RoleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHeaderDescriptionExpanded, setIsHeaderDescriptionExpanded] =
    useState(false);
  const [isHeaderDescriptionTruncated, setIsHeaderDescriptionTruncated] =
    useState(false);
  const headerDescriptionRef = useRef(null);
  const { hasPermission } = useContext(AuthContext);

  const canViewRoles = hasPermission("Access Control", "Roles", "GET");
  const canEditRoles = hasPermission("Access Control", "Roles", "PATCH");
  const canDeleteRoles = hasPermission("Access Control", "Roles", "DELETE");
  const [dashboardData, setDashboardData] = useState({ cards: [] });
  const isSuperAdmin = role?.roleName?.toLowerCase() === "superadmin";
  const [originalSelectedRoles, setOriginalSelectedRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const initialSelectionSet = useRef(false);
  const toast = useToast();

  useEffect(() => {
    const isTruncated = (element) => {
      if (!element) return false;
      return (
        element.scrollHeight > element.clientHeight + 1 ||
        element.scrollWidth > element.clientWidth + 1
      );
    };

    const updateTruncationState = () => {
      if (!isHeaderDescriptionExpanded) {
        setIsHeaderDescriptionTruncated(isTruncated(headerDescriptionRef.current));
      }
    };

    const rafId = window.requestAnimationFrame(updateTruncationState);
    window.addEventListener("resize", updateTruncationState);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateTruncationState);
    };
  }, [
    role?.roleDescription,
    isHeaderDescriptionExpanded,
  ]);

  useEffect(() => {
    loadRole();
  }, [id]);

  const loadRole = async () => {
    try {
      const dashboardRes = await getDashboardData();
      setDashboardData(dashboardRes);

      const data = await getRoleById(id);
      setRole(
        data
          ? {
            ...data,
            roleAccess: normalizeRoleAccessSections(data.roleAccess),
          }
          : {
            roleAccess: [],
          },
      );

      // 👇 Only for SuperAdmin
      if (data.roleName?.toLowerCase() === "superadmin") {
        const roles = await fetchRoles();

        const filtered = roles.filter(
          (r) => r.roleName.toLowerCase() !== "superadmin"
        );

        setAllRoles(filtered);

        // Preselect roles already flagged (only on initial load)
        if (!initialSelectionSet.current) {
          const preSelected = filtered
            .filter(r => r.canViewDashboardAs === true)
            .map(r => r._id);

          setSelectedRoles(preSelected);
          setOriginalSelectedRoles(preSelected);
        }
      }

    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!role?._id) return;
    setIsDeleting(true);
    try {
      await deleteRole(role._id);
      navigate("/settings/roles/manage");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!canViewRoles) return <Unauthorized />;
  if (loading) return <Loading />;
  if (!role) return null;

  return (
    <Box p={6} bg="#F5F7FA" minH="100vh">
      {/* Breadcrumb */}
      <BreadCrumbMaker
        items={[
          { title: "Access Control", link: "/settings" },
          { title: "Manage Roles", link: "/settings/roles/manage" },
          { title: "Role Details", link: `/settings/roles/${id}` },
        ]}
      />

      <Box bg="white" borderRadius="2xl" p={6} mt={2} overflow="hidden">
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6}>
          <Flex align="center" gap={3} flex={1} minW={0}>
            <Text
              fontSize="2xl"
              fontWeight="700"
              isTruncated
              maxW="100%"
              overflowWrap="anywhere"
              wordBreak="break-all"
              title={role.roleName}
            >
              {role.roleName}
            </Text>

            {role.roleName.toLowerCase() === "superadmin" && (
              <Badge colorScheme="red" fontSize="sm" flexShrink={0}>
                System Role
              </Badge>
            )}
          </Flex>

          <Flex align="center" gap={2}>
            {canEditRoles && role.roleName.toLowerCase() !== "superadmin" && (
              <Button
                size="sm"
                bg="#4C7CF7"
                color="white"
                borderRadius="8px"
                _hover={{ bg: "#3b6ae0" }}
                onClick={() => navigate(`/settings/roles/edit/${id}`)}
              >
                Edit Role
              </Button>
            )}

            {canDeleteRoles &&
              role.roleName.toLowerCase() !== "superadmin" &&
              (role.assignedAdminsCount > 0 ? (
                <Tooltip
                  label={`This role is assigned to ${role.assignedAdminsCount} admin${role.assignedAdminsCount > 1 ? "s" : ""} and cannot be deleted`}
                  bg="gray.900"
                  color="white"
                  borderRadius="8px"
                  fontSize="sm"
                  px={3}
                  py={2}
                  placement="top"
                  left={-5}
                >
                  <span>
                    <Button
                      size="sm"
                      bg="#DC2626"
                      color="white"
                      isDisabled
                      opacity={0.6}
                      cursor="not-allowed"
                      _hover={{ bg: "#DC2626" }}
                    >
                      Delete Role
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  size="sm"
                  bg="#DC2626"
                  color="white"
                  _hover={{ bg: "#B91C1C" }}
                  onClick={() => setIsDeleteOpen(true)}
                >
                  Delete Role
                </Button>
              ))}
          </Flex>
        </Flex>

        <Text
          ref={headerDescriptionRef}
          color="gray.600"
          mb={2}
          overflowWrap="anywhere"
          wordBreak="break-all"
          whiteSpace="normal"
          maxW="100%"
          noOfLines={isHeaderDescriptionExpanded ? undefined : 2}
        >
          {role.roleDescription || "—"}
        </Text>
        {role.roleDescription && isHeaderDescriptionTruncated && (
          <Button
            variant="link"
            size="sm"
            colorScheme="blue"
            mb={6}
            onClick={() => setIsHeaderDescriptionExpanded((prev) => !prev)}
          >
            {isHeaderDescriptionExpanded ? "View less" : "View more"}
          </Button>
        )}
        {isSuperAdmin && (
          <Box mt={6} mb={8}>
            <Text fontWeight="600" color="gray.500" fontSize="sm" mb={3}>
              Select roles allowed to view dashboard
            </Text>

            <Box
              maxH="250px"
              overflowY="auto"
              border="1px solid #E5E7EB"
              borderRadius="md"
              p={3}
              bg="#F9FAFB"
            >
              {allRoles.map((r) => (
                <Box key={r._id} mb={2}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(r._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRoles((prev) => [...prev, r._id]);
                        } else {
                          setSelectedRoles((prev) =>
                            prev.filter((id) => id !== r._id)
                          );
                        }
                      }}
                    />
                    <span style={{ marginLeft: 8 }}>{r.roleName}</span>
                  </label>
                </Box>
              ))}
            </Box>

            <Button
              mt={4}
              size="sm"
              colorScheme="blue"
              onClick={async () => {
                const token = localStorage.getItem("prima_session");

                // Roles newly checked (false → true)
                const added = selectedRoles.filter(
                  id => !originalSelectedRoles.includes(id)
                );

                // Roles unchecked (true → false)
                const removed = originalSelectedRoles.filter(
                  id => !selectedRoles.includes(id)
                );

                const changedRoleIds = [...added, ...removed];

                if (changedRoleIds.length === 0) {
                  toast({
                    title: "No changes detected",
                    status: "info",
                    duration: 2000,
                  });
                  return;
                }

                for (let roleId of changedRoleIds) {
                  await updateRole(
                    roleId,
                    {
                      canViewDashboardAs: selectedRoles.includes(roleId),
                    },
                    token
                  );
                }

                setOriginalSelectedRoles(selectedRoles);

                toast({
                  title: "Dashboard visibility updated",
                  status: "success",
                  duration: 3000,
                  isClosable: true,
                });
              }}
            >
              Save Dashboard Role Visibility
            </Button>
          </Box>
        )}

        {/* 🔥 CUSTOM TABS (LIKE SCREENSHOT) */}
        <Tabs variant="unstyled">
          <TabList
            bg="#EEF2FF"
            p={1}
            borderRadius="16px"
            display="flex"
            gap={1}
            mb={6}
          >
            {["Details", "Permissions"].map((label) => (
              <Tab
                key={label}
                px={6}
                py={3}
                fontSize="16px"
                fontWeight="600"
                borderRadius="12px"
                color="#2D336B"
                _selected={{
                  bg: "white",
                  boxShadow: "sm",
                }}
                _hover={{ bg: "whiteAlpha.700" }}
              >
                {label}
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {/* DETAILS TAB */}
            <TabPanel p={0}>
              <Grid templateColumns="repeat(2,1fr)" gap={6}>
                <GridItem>
                  <Text fontWeight="600" color="gray.500" fontSize="sm">
                    CREATED AT
                  </Text>
                  <Text fontSize="lg" mt={1}>
                    {new Date(role.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="600" color="gray.500" fontSize="sm">
                    ASSIGNED ADMINS
                  </Text>
                  <Text fontSize="lg" mt={1}>
                    {role.assignedAdminsCount ?? 0}
                  </Text>
                </GridItem>
              </Grid>
            </TabPanel>

            {/* PERMISSIONS TAB */}
            <TabPanel p={0}>
              {role.dashboardAccessKeys?.length > 0 && (
                <Box mb={8}>
                  <Text fontSize="lg" fontWeight="700" mb={4} color="#1A202C">
                    Dashboard Cards
                  </Text>
                  <Flex gap={4} wrap="wrap" className="p-4 rounded">
                    {role.dashboardAccessKeys.map((cardKey) => {
                      const matchedCard = dashboardData.cards?.find(
                        (card) => card.section === cardKey,
                      );
                      const cardTitle = matchedCard?.title || cardKey;
                      return (
                        <Badge
                          key={cardKey}
                          bg="#E7EDFF"
                          color="#1D3B8B"
                          borderRadius="full"
                          px={3}
                          py={1}
                          fontSize="sm"
                          fontWeight="700"
                          letterSpacing="0.3px"
                          textTransform="uppercase"
                        >
                          {cardTitle}
                        </Badge>
                      );
                    })}
                  </Flex>
                </Box>
              )}
              {role.roleAccess.map((section, sIdx) => (
                <Box key={section._id || sIdx} mb={8}>
                  <Text fontSize="lg" fontWeight="700" mb={4} color="#1A202C">
                    {section.heading}
                  </Text>

                  <TableContainer
                    border="1px solid #E5E7EB"
                    borderRadius="12px"
                    overflow="hidden"
                  >
                    <Table size="sm">
                      <Thead bg="#F9FAFB">
                        <Tr>
                          <Th>Permission</Th>
                          <Th textAlign="center">Create</Th>
                          <Th textAlign="center">View</Th>
                          <Th textAlign="center">Edit</Th>
                          <Th textAlign="center">Delete</Th>
                        </Tr>
                      </Thead>

                      <Tbody>
                        {section.types.map((type, tIdx) => (
                          <Tr key={type._id || tIdx}>
                            <Td fontWeight="500">{type.subHeading}</Td>
                            {["POST", "GET", "PATCH", "DELETE"].map((perm) => (
                              <Td
                                key={perm}
                                textAlign="center"
                                color={
                                  type.access.includes(perm)
                                    ? "green.500"
                                    : "gray.400"
                                }
                                fontWeight="700"
                              >
                                {type.access.includes(perm) ? "✓" : "—"}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <DeleteAlert
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={handleDeleteRole}
        label="Role"
      />
    </Box>
  );
}
